import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Tone } from "@/components/emc/status";
import { TONE_TEXT } from "@/components/emc/tone";

// A tile is a card of its own in a grid, or one cell of a strip — one card
// with the figures side by side and a hairline between them (UI-8).
const StripContext = createContext(false);

interface Props {
  label: ReactNode;
  /** The number, already formatted: `2 801 172`, `0,4 %`, `—`. */
  value: ReactNode;
  /** A number comes with what it is measured against — "a napi keret
   * 52%-a", not just the number. */
  comparison?: ReactNode;
  /** Colour of the comparison (and of the icon), when it says something. */
  tone?: Tone;
  /** A sparkline or a limit bar under the number. */
  trend?: ReactNode;
  /** A small visual beside the number: a ring, a sparkline. */
  aside?: ReactNode;
  /** An icon before the label, in the tone's colour. */
  icon?: ReactNode;
  /** Smaller, for a strip of many tiles. */
  size?: "default" | "sm";
  className?: string;
}

/** A KPI: a big tabular number, what it counts, and what it is compared to. */
export function StatTile({
  label,
  value,
  comparison,
  tone = "muted",
  trend,
  aside,
  icon,
  size = "default",
  className,
}: Props) {
  const inStrip = useContext(StripContext);
  return (
    <div
      data-slot="stat-tile"
      className={cn(
        "flex min-w-0 items-start gap-3",
        !inStrip && "rounded-card bg-card shadow-card",
        size === "default" ? "px-5 py-4" : "px-4 py-3",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            "flex min-w-0 items-center gap-1.5 text-detail font-medium [&_svg]:size-3.75 [&_svg]:shrink-0",
            icon && tone !== "muted" ? TONE_TEXT[tone] : "text-muted-foreground",
          )}
        >
          {icon}
          <span className="truncate">{label}</span>
        </span>
        <span
          className={cn(
            "whitespace-nowrap text-foreground tabular-nums",
            size === "default" ? "text-number font-title" : "text-heading font-semibold",
          )}
        >
          {value}
        </span>
        {comparison && (
          <span className={cn("text-label", TONE_TEXT[tone])}>{comparison}</span>
        )}
        {trend && <div className="mt-1.5">{trend}</div>}
      </div>
      {aside && <div className="shrink-0 self-center">{aside}</div>}
    </div>
  );
}

/** Tiles in a row that wraps (`grid`, each its own card), or one card with
 * the tiles side by side and a hairline between them (`strip`). */
export function StatGrid({
  children,
  variant = "grid",
  className,
}: {
  children: ReactNode;
  variant?: "grid" | "strip";
  className?: string;
}) {
  if (variant === "strip") {
    return (
      <StripContext.Provider value>
        <div
          data-slot="stat-grid"
          data-variant="strip"
          className={cn(
            "grid grid-cols-[repeat(auto-fit,minmax(var(--emc-tile-min),1fr))] overflow-hidden rounded-card bg-card py-1 shadow-card",
            "[&>[data-slot=stat-tile]]:border-l [&>[data-slot=stat-tile]]:border-separator [&>[data-slot=stat-tile]]:-ml-px",
            className,
          )}
        >
          {children}
        </div>
      </StripContext.Provider>
    );
  }
  return (
    <div
      data-slot="stat-grid"
      className={cn("grid grid-cols-[repeat(auto-fill,minmax(var(--emc-tile-min),1fr))] gap-4", className)}
    >
      {children}
    </div>
  );
}
